// Commitment Tracker hooks — FE-041 / FE-043.

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  addCommitmentComment,
  addCommitmentDependency,
  archiveCommitment,
  assignCommitment,
  blockCommitment,
  createCommitment,
  createSavedFilter,
  deleteCommitment,
  deleteCommitmentComment,
  deleteSavedFilter,
  restoreCommitment,
  fetchCommitmentComments,
  fetchCommitmentDetail,
  fetchCommitments,
  fetchMembers,
  createTag,
  fetchCategoryPicker,
  fetchCommitmentAttachments,
  fetchPlanYearPicker,
  fetchSavedFilters,
  fetchTagPicker,
  finalizeCommitmentAttachment,
  linkCommitmentAttachment,
  previewCommitmentAttachment,
  unlinkCommitmentAttachment,
  removeCommitmentDependency,
  reviewCommitment,
  tagCommitment,
  transitionCommitment,
  unblockCommitment,
  untagCommitment,
  unwatchCommitment,
  updateCommitment,
  updateSavedFilter,
  watchCommitment,
  type AssignCommitmentInput,
  type CategoryPickerRow,
  type CreateSavedFilterInput,
  type CreateTagInput,
  type CreatedTag,
  type MemberPickerRow,
  type PlanYearPickerRow,
  type SavedFilterDto,
  type SavedFilterListResponse,
  type TagPickerRow,
  type UpdateSavedFilterInput,
} from "./api";
import {
  CommitmentNotFoundError,
  type AddCommitmentCommentInput,
  type AddCommitmentCommentResponse,
  type CommitmentCommentsListResponse,
  type CommitmentDetail,
  type CommitmentListQuery,
  type CommitmentListResponse,
  type CommitmentListRow,
  type CommitmentMutationResponse,
  type CommitmentStatus,
  type CommitmentTransitionInput,
  type CreateCommitmentInput,
  type UpdateCommitmentInput,
} from "./types";


export const COMMITMENTS_QUERY_KEY = "commitments" as const;


export function useCommitmentListQuery(
  query: CommitmentListQuery,
): UseQueryResult<CommitmentListResponse> {
  return useQuery({
    queryKey: [COMMITMENTS_QUERY_KEY, "list", query],
    queryFn: ({ signal }) => fetchCommitments(query, { signal }),
  });
}


/** Per-status infinite-scroll list query for the grouped Tracker
 * view. Each visible status group calls this with its own bucket; the
 * BE keeps ``status_counts`` independent of the bucket filter, so the
 * group headers can show full-distribution counts while only one
 * bucket of rows lands per group.
 *
 * The page size is intentionally larger than the flat-pager default
 * (50 vs 25) — once the user opens a group they want to scan, and
 * the cost of pulling 50 rows per scroll page is small.
 */
export const COMMITMENTS_GROUP_PAGE_SIZE = 50 as const;

export function useCommitmentBucketInfiniteQuery(args: {
  status: CommitmentStatus;
  base: Omit<CommitmentListQuery, "limit" | "offset">;
  enabled?: boolean;
}): UseInfiniteQueryResult<
  { pages: CommitmentListResponse[]; pageParams: number[] },
  Error
> {
  const { status, base, enabled = true } = args;
  // The bucket filter sits in ``filters.status`` so the BE applies it
  // to the page slice; ``status_counts`` is unaffected.
  const queryArg: CommitmentListQuery = {
    ...base,
    filters: { ...base.filters, status },
    limit: COMMITMENTS_GROUP_PAGE_SIZE,
    offset: 0,
  };
  return useInfiniteQuery({
    queryKey: [
      COMMITMENTS_QUERY_KEY, "bucket", status, queryArg,
    ],
    enabled,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      fetchCommitments(
        { ...queryArg, offset: pageParam as number },
        { signal },
      ),
    getNextPageParam: (lastPage, allPages) => {
      const fetchedSoFar = allPages.reduce(
        (n, p) => n + p.rows.length, 0,
      );
      if (fetchedSoFar >= lastPage.total) return undefined;
      return fetchedSoFar;
    },
  });
}


export function useCommitmentDetailQuery(
  commitmentId: string | undefined,
): UseQueryResult<CommitmentDetail> {
  return useQuery({
    queryKey: [COMMITMENTS_QUERY_KEY, "detail", commitmentId],
    queryFn: ({ signal }) => fetchCommitmentDetail(commitmentId!, { signal }),
    enabled: Boolean(commitmentId),
    retry: (failureCount, error) =>
      !(error instanceof CommitmentNotFoundError) && failureCount < 1,
  });
}


// ── Cache invalidation helper ─────────────────────────────────────────────


function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  // Lists feed both the Tracker (FE-041 / FE-042) and dashboard
  // widgets that read commitment state. Bust them all on any
  // mutation; the queries will refetch lazily as their pages mount.
  //
  // ``"bucket"`` is its own per-status prefix that powers the Kanban
  // columns and the grouped list view; it must be busted alongside
  // ``"list"`` or a status change leaves the moved card stuck in its
  // old column / group.
  queryClient.invalidateQueries({
    queryKey: [COMMITMENTS_QUERY_KEY, "list"],
  });
  queryClient.invalidateQueries({
    queryKey: [COMMITMENTS_QUERY_KEY, "bucket"],
  });
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
}


export function useCreateCommitmentMutation(): UseMutationResult<
  CommitmentMutationResponse, Error, CreateCommitmentInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCommitmentInput) => createCommitment(input),
    onSuccess: () => invalidateAll(queryClient),
  });
}


export interface UpdateCommitmentVariables {
  commitment_id: string;
  values: UpdateCommitmentInput;
}


export function useUpdateCommitmentMutation(): UseMutationResult<
  CommitmentMutationResponse, Error, UpdateCommitmentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) => updateCommitment(vars.commitment_id, vars.values),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", vars.commitment_id],
      });
      invalidateAll(queryClient);
    },
  });
}


export interface TransitionCommitmentVariables extends CommitmentTransitionInput {}


export function useTransitionCommitmentMutation(
  commitmentId: string,
): UseMutationResult<
  CommitmentMutationResponse, Error, TransitionCommitmentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => transitionCommitment(commitmentId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", commitmentId],
      });
      invalidateAll(queryClient);
    },
  });
}


// ── Kanban movement (FE-042b) ─────────────────────────────────────────────
//
// Same backend call as ``useTransitionCommitmentMutation`` but shaped
// for the board, where the moved card's id arrives in the variables
// rather than being baked in at hook creation time. Wraps the call
// with optimistic list-cache patching so the card jumps to its new
// column on mouse-up; rollback restores the snapshot on failure so
// the board never shows a stale state.


export interface KanbanTransitionVariables {
  commitment_id: string;
  to_status: CommitmentStatus;
  cancellation_reason?: string | null;
}


interface KanbanTransitionContext {
  /** Snapshots of every list-cache entry we touched so ``onError``
   * can fully restore them — there can be more than one (board view
   * vs. dashboard widgets querying the same key prefix). */
  snapshots: Array<readonly [readonly unknown[], CommitmentListResponse]>;
  /** The status the card was in before the move so the page-level
   * cancellation flow can rollback its modal state on failure. */
  previousStatus: CommitmentStatus | null;
}


export function useKanbanTransitionMutation(): UseMutationResult<
  CommitmentMutationResponse,
  Error,
  KanbanTransitionVariables,
  KanbanTransitionContext
> {
  const queryClient = useQueryClient();
  return useMutation<
    CommitmentMutationResponse,
    Error,
    KanbanTransitionVariables,
    KanbanTransitionContext
  >({
    mutationFn: (vars) =>
      transitionCommitment(vars.commitment_id, {
        to_status: vars.to_status,
        ...(vars.cancellation_reason
          ? { cancellation_reason: vars.cancellation_reason }
          : {}),
      }),
    onMutate: async (vars) => {
      // Cancel any in-flight list refetches so they don't race the
      // optimistic patch.
      await queryClient.cancelQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "list"],
      });
      const matches = queryClient.getQueriesData<CommitmentListResponse>({
        queryKey: [COMMITMENTS_QUERY_KEY, "list"],
      });
      const snapshots: KanbanTransitionContext["snapshots"] = [];
      let previousStatus: CommitmentStatus | null = null;
      for (const [key, data] of matches) {
        if (!data) continue;
        snapshots.push([key, data]);
        const nextRows: CommitmentListRow[] = data.rows.map((row) => {
          if (row.commitment_id !== vars.commitment_id) return row;
          if (previousStatus === null) previousStatus = row.status;
          return { ...row, status: vars.to_status };
        });
        queryClient.setQueryData<CommitmentListResponse>(key, {
          ...data,
          rows: nextRows,
        });
      }
      return { snapshots, previousStatus };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.snapshots) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: (_data, _err, vars) => {
      // Always reconcile against the server: even on success, BE may
      // have side-effects (started_at / completed_at) that change the
      // card surface beyond just ``status``.
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", vars.commitment_id],
      });
      invalidateAll(queryClient);
    },
  });
}


export function useDeleteCommitmentMutation(
  commitmentId: string,
): UseMutationResult<CommitmentMutationResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteCommitment(commitmentId),
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", commitmentId],
      });
      invalidateAll(queryClient);
    },
  });
}


export function useArchiveCommitmentMutation(
  commitmentId: string,
): UseMutationResult<CommitmentMutationResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => archiveCommitment(commitmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", commitmentId],
      });
      invalidateAll(queryClient);
    },
  });
}


export function useRestoreCommitmentMutation(
  commitmentId: string,
): UseMutationResult<CommitmentMutationResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => restoreCommitment(commitmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", commitmentId],
      });
      invalidateAll(queryClient);
    },
  });
}


export interface BlockCommitmentVariables {
  blocked_reason: string;
}


export function useBlockCommitmentMutation(
  commitmentId: string,
): UseMutationResult<
  CommitmentMutationResponse, Error, BlockCommitmentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) => blockCommitment(commitmentId, vars.blocked_reason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", commitmentId],
      });
      invalidateAll(queryClient);
    },
  });
}


export function useUnblockCommitmentMutation(
  commitmentId: string,
): UseMutationResult<CommitmentMutationResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unblockCommitment(commitmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", commitmentId],
      });
      invalidateAll(queryClient);
    },
  });
}


/** Lightweight assign mutation — variables carry the commitmentId
 * so the same hook can be used to drive list-row pickers and the
 * detail page without re-instantiating per row. */
export interface AssignCommitmentVariables {
  commitment_id: string;
  input: AssignCommitmentInput;
}


export function useAssignCommitmentMutation(): UseMutationResult<
  CommitmentMutationResponse, Error, AssignCommitmentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) => assignCommitment(vars.commitment_id, vars.input),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", vars.commitment_id],
      });
      invalidateAll(queryClient);
    },
  });
}


export interface TagCommitmentVariables {
  commitment_id: string;
  tag_id: string;
}


export function useTagCommitmentMutation(): UseMutationResult<
  CommitmentMutationResponse, Error, TagCommitmentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) => tagCommitment(vars.commitment_id, vars.tag_id),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", vars.commitment_id],
      });
      invalidateAll(queryClient);
    },
  });
}


export function useUntagCommitmentMutation(): UseMutationResult<
  CommitmentMutationResponse, Error, TagCommitmentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) => untagCommitment(vars.commitment_id, vars.tag_id),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", vars.commitment_id],
      });
      invalidateAll(queryClient);
    },
  });
}


export interface WatchCommitmentVariables {
  commitment_id: string;
  user_id?: string | null;
}


export function useWatchCommitmentMutation(): UseMutationResult<
  CommitmentMutationResponse, Error, WatchCommitmentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) => watchCommitment(vars.commitment_id, vars.user_id),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", vars.commitment_id],
      });
      invalidateAll(queryClient);
    },
  });
}


export interface ReviewCommitmentVariables {
  commitment_id: string;
  reviewed: boolean;
}


export function useReviewCommitmentMutation(): UseMutationResult<
  CommitmentMutationResponse, Error, ReviewCommitmentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      reviewCommitment(vars.commitment_id, vars.reviewed),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", vars.commitment_id],
      });
      invalidateAll(queryClient);
    },
  });
}


export interface AddDependencyVariables {
  commitment_id: string;
  depends_on_commitment_id: string;
}


export function useAddCommitmentDependencyMutation(): UseMutationResult<
  CommitmentMutationResponse, Error, AddDependencyVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      addCommitmentDependency(
        vars.commitment_id, vars.depends_on_commitment_id,
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", vars.commitment_id],
      });
      invalidateAll(queryClient);
    },
  });
}


export interface RemoveDependencyVariables {
  commitment_id: string;
  dependency_id: string;
}


export function useRemoveCommitmentDependencyMutation(): UseMutationResult<
  CommitmentMutationResponse, Error, RemoveDependencyVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      removeCommitmentDependency(
        vars.commitment_id, vars.dependency_id,
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", vars.commitment_id],
      });
      invalidateAll(queryClient);
    },
  });
}


export function useUnwatchCommitmentMutation(): UseMutationResult<
  CommitmentMutationResponse, Error, WatchCommitmentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      unwatchCommitment(vars.commitment_id, vars.user_id),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENTS_QUERY_KEY, "detail", vars.commitment_id],
      });
      invalidateAll(queryClient);
    },
  });
}


// ── Saved filters + pickers (Polish B2) ───────────────────────────────────


export const SAVED_FILTERS_QUERY_KEY = "commitments-saved-filters" as const;
export const MEMBERS_PICKER_QUERY_KEY = "commitments-members-picker" as const;
export const TAGS_PICKER_QUERY_KEY = "commitments-tags-picker" as const;
export const CATEGORIES_PICKER_QUERY_KEY =
  "commitments-categories-picker" as const;


export function useSavedFiltersQuery(
  scope = "commitment",
): UseQueryResult<SavedFilterListResponse> {
  return useQuery({
    queryKey: [SAVED_FILTERS_QUERY_KEY, scope],
    queryFn: ({ signal }) => fetchSavedFilters(scope, { signal }),
  });
}


export function useCreateSavedFilterMutation(): UseMutationResult<
  SavedFilterDto, Error, CreateSavedFilterInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => createSavedFilter(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SAVED_FILTERS_QUERY_KEY],
      });
    },
  });
}


export interface UpdateSavedFilterVariables {
  id: string;
  input: UpdateSavedFilterInput;
}


export function useUpdateSavedFilterMutation(): UseMutationResult<
  SavedFilterDto, Error, UpdateSavedFilterVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) => updateSavedFilter(vars.id, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SAVED_FILTERS_QUERY_KEY],
      });
    },
  });
}


export function useDeleteSavedFilterMutation(): UseMutationResult<
  void, Error, string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteSavedFilter(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SAVED_FILTERS_QUERY_KEY],
      });
    },
  });
}


export function useMembersPickerQuery(
  enabled = true,
): UseQueryResult<{ members: MemberPickerRow[] }> {
  return useQuery({
    queryKey: [MEMBERS_PICKER_QUERY_KEY],
    queryFn: ({ signal }) => fetchMembers({ signal }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}


export interface AccountPickerRow {
  id: string;
  legal_name: string;
  dba_name: string | null;
}


export const ACCOUNTS_PICKER_QUERY_KEY = "commitments-accounts-picker" as const;


/** Lightweight Account picker — reuses the FE-038 list to pull the
 * top N Accounts the broker has on file. Cached 5min like the other
 * pickers; the popover's search input filters client-side. */
export function useAccountsPickerQuery(
  enabled = true,
): UseQueryResult<{ accounts: AccountPickerRow[] }> {
  return useQuery({
    queryKey: [ACCOUNTS_PICKER_QUERY_KEY],
    queryFn: async ({ signal }) => {
      // Late import — avoid a top-level cycle between commitments
      // and accounts modules.
      const { fetchAccountList } = await import(
        "@/features/accounts/api"
      );
      const resp = await fetchAccountList(
        {
          filters: {},
          sortBy: "legal_name",
          sortDir: "asc",
          limit: 200,
          offset: 0,
        },
        { signal },
      );
      return {
        accounts: resp.rows.map((r) => ({
          id: r.account_id,
          legal_name: r.legal_name,
          dba_name: r.dba_name ?? null,
        })),
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}


export function useTagsPickerQuery(
  enabled = true,
): UseQueryResult<{ tags: TagPickerRow[] }> {
  return useQuery({
    queryKey: [TAGS_PICKER_QUERY_KEY],
    queryFn: ({ signal }) => fetchTagPicker({ signal }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}


export function useCategoriesPickerQuery(
  enabled = true,
): UseQueryResult<{ categories: CategoryPickerRow[] }> {
  return useQuery({
    queryKey: [CATEGORIES_PICKER_QUERY_KEY],
    queryFn: ({ signal }) => fetchCategoryPicker({ signal }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}


export function useCreateTagMutation(): UseMutationResult<
  CreatedTag, Error, CreateTagInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => createTag(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TAGS_PICKER_QUERY_KEY],
      });
    },
  });
}


export const PLAN_YEAR_PICKER_QUERY_KEY = "commitments-plan-year-picker" as const;


export function usePlanYearPickerQuery(
  accountId: string | null,
  enabled = true,
): UseQueryResult<{ plan_years: PlanYearPickerRow[] }> {
  return useQuery({
    queryKey: [PLAN_YEAR_PICKER_QUERY_KEY, accountId ?? null],
    queryFn: ({ signal }) =>
      fetchPlanYearPicker(accountId, { signal }),
    enabled: enabled && Boolean(accountId),
    staleTime: 5 * 60 * 1000,
  });
}


// ── FE-048 — Commitment attachments ─────────────────────────────────────


export const COMMITMENT_ATTACHMENTS_QUERY_KEY = (
  "commitment-attachments" as const
);


export function useCommitmentAttachmentsQuery(
  commitmentId: string,
): UseQueryResult<
  import("./types").CommitmentAttachmentsListResponse
> {
  return useQuery({
    queryKey: [COMMITMENT_ATTACHMENTS_QUERY_KEY, commitmentId],
    queryFn: ({ signal }) =>
      fetchCommitmentAttachments(commitmentId, { signal }),
    enabled: commitmentId.length > 0,
  });
}


function invalidateAttachments(
  qc: ReturnType<typeof useQueryClient>, commitmentId: string,
): void {
  qc.invalidateQueries({
    queryKey: [COMMITMENT_ATTACHMENTS_QUERY_KEY, commitmentId],
  });
}


export function useLinkCommitmentAttachmentMutation(
  commitmentId: string,
): UseMutationResult<
  import("./types").CommitmentAttachmentLinkResponse, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) =>
      linkCommitmentAttachment(commitmentId, fileId),
    onSuccess: () => invalidateAttachments(qc, commitmentId),
  });
}


export function useFinalizeCommitmentAttachmentMutation(
  commitmentId: string,
): UseMutationResult<
  import("./types").CommitmentAttachmentLinkResponse,
  Error,
  Parameters<typeof finalizeCommitmentAttachment>[1]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      finalizeCommitmentAttachment(commitmentId, payload),
    onSuccess: () => invalidateAttachments(qc, commitmentId),
  });
}


export function useUnlinkCommitmentAttachmentMutation(
  commitmentId: string,
): UseMutationResult<
  { link_id: string; unlinked: boolean }, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) =>
      unlinkCommitmentAttachment(commitmentId, linkId),
    onSuccess: () => invalidateAttachments(qc, commitmentId),
  });
}


export function usePreviewCommitmentAttachment(
  commitmentId: string,
): (
  linkId: string,
  opts?: { disposition?: "inline" | "attachment" },
) => Promise<import("./types").CommitmentAttachmentPreviewResponse> {
  return (linkId, opts) =>
    previewCommitmentAttachment(commitmentId, linkId, opts);
}


// ── Commitments-01c — Comments ───────────────────────────────────────────


export const COMMITMENT_COMMENTS_QUERY_KEY = "commitment-comments" as const;


export function useCommitmentCommentsQuery(
  commitmentId: string | undefined,
): UseQueryResult<CommitmentCommentsListResponse> {
  return useQuery({
    queryKey: [COMMITMENT_COMMENTS_QUERY_KEY, commitmentId],
    queryFn: ({ signal }) =>
      fetchCommitmentComments(commitmentId!, { signal }),
    enabled: Boolean(commitmentId),
    staleTime: 30 * 1000,
  });
}


function invalidateComments(
  qc: ReturnType<typeof useQueryClient>, commitmentId: string,
) {
  qc.invalidateQueries({
    queryKey: [COMMITMENT_COMMENTS_QUERY_KEY, commitmentId],
  });
}


export function useAddCommitmentCommentMutation(
  commitmentId: string,
): UseMutationResult<
  AddCommitmentCommentResponse, Error, AddCommitmentCommentInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => addCommitmentComment(commitmentId, input),
    onSuccess: (resp) => {
      // BeneAI-mentions: when the response carries an ``agent_reply``
      // (i.e. the body contained ``@BeneAI`` and the runtime replied
      // synchronously), splice BOTH rows directly into the cache so
      // the agent comment lands without waiting for the invalidation
      // refetch to round-trip. Falling back to ``invalidateComments``
      // keeps every other path on the same code path.
      const cacheKey = [COMMITMENT_COMMENTS_QUERY_KEY, commitmentId];
      const existing =
        qc.getQueryData<CommitmentCommentsListResponse>(cacheKey);
      if (existing) {
        const seen = new Set(existing.rows.map((r) => r.comment_id));
        const next = existing.rows.slice();
        if (!seen.has(resp.comment_id)) {
          next.push(resp);
          seen.add(resp.comment_id);
        }
        const agentReply = resp.agent_reply;
        if (agentReply && !seen.has(agentReply.comment_id)) {
          next.push(agentReply);
        }
        qc.setQueryData<CommitmentCommentsListResponse>(cacheKey, {
          rows: next,
          total: next.length,
        });
      }
      invalidateComments(qc, commitmentId);
    },
  });
}


export function useDeleteCommitmentCommentMutation(
  commitmentId: string,
): UseMutationResult<
  { comment_id: string; deleted: boolean }, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      deleteCommitmentComment(commitmentId, commentId),
    onSuccess: () => invalidateComments(qc, commitmentId),
  });
}
